import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { X, Undo2, RotateCcw, Check, Mic, Type, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const COLORS = [
  { key: "red", val: "#ef4444" },
  { key: "yellow", val: "#facc15" },
  { key: "white", val: "#ffffff" },
];
const SIZES = ["s", "m", "l"];
const STROKE_MUL = { s: 0.8, m: 1.5, l: 2.6 };
const TEXT_MUL = { s: 0.75, m: 1.05, l: 1.55 };

const haptic = () => { try { navigator.vibrate?.(12); } catch { /* ignore */ } };

// Smart-draw photo annotator.
//  • Draw a straight stroke  -> snaps to an arrow (head at where the finger first presses)
//  • Draw a closed loop      -> snaps to a clean ellipse
//  • Two quick crossing lines -> snaps to an X
//  • Anything else           -> stays freehand
//  • Long-press a mark        -> add a text label (or edit a text mark)
//  • Tap / drag a mark        -> select / move it, then recolor & resize
export function PhotoAnnotator({ files, onSaveEach, onDone, onCancel, title = "Mark up photo", startLocked = false }) {
  const list = Array.from(files || []);
  const [idx, setIdx] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [color, setColor] = useState("red");
  const [colorOpen, setColorOpen] = useState(false);
  const [size, setSize] = useState("l");
  const [drawOn, setDrawOn] = useState(!startLocked);
  const [selId, setSelId] = useState(null);
  const [hint, setHint] = useState(true);
  const [confirmClose, setConfirmClose] = useState(false);
  const [textModal, setTextModal] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recTimerRef = useRef(null);

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const actionsRef = useRef([]);
  const draftRef = useRef(null);
  const dragRef = useRef(null);
  const longRef = useRef(null);
  const idRef = useRef(0);

  const colorVal = (k) => (COLORS.find((c) => c.key === k) || COLORS[0]).val;
  const strokeW = (a, c) => Math.max(2, (c.width / 260) * STROKE_MUL[a.size || "m"]);
  const find = (id) => actionsRef.current.find((a) => a.id === id);

  /* ---------- drawing primitives ---------- */
  const arrowHead = (ctx, x, y, angle, s) => {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - s * Math.cos(angle - Math.PI / 7), y - s * Math.sin(angle - Math.PI / 7));
    ctx.moveTo(x, y);
    ctx.lineTo(x - s * Math.cos(angle + Math.PI / 7), y - s * Math.sin(angle + Math.PI / 7));
    ctx.stroke();
  };

  const drawShape = (ctx, a, c) => {
    const col = colorVal(a.color);
    const w = strokeW(a, c);
    ctx.strokeStyle = col; ctx.fillStyle = col;
    ctx.lineWidth = w; ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (a.type === "stroke") {
      ctx.beginPath();
      a.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else if (a.type === "circle") {
      ctx.beginPath();
      ctx.ellipse(a.x + a.w / 2, a.y + a.h / 2, Math.max(1, a.w / 2), Math.max(1, a.h / 2), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (a.type === "x") {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y); ctx.lineTo(a.x + a.w, a.y + a.h);
      ctx.moveTo(a.x + a.w, a.y); ctx.lineTo(a.x, a.y + a.h);
      ctx.stroke();
    } else if (a.type === "arrow") {
      const angle = Math.atan2(a.y2 - a.y1, a.x2 - a.x1);
      const head = Math.max(18, w * 5);
      ctx.beginPath(); ctx.moveTo(a.x1, a.y1); ctx.lineTo(a.x2, a.y2); ctx.stroke();
      arrowHead(ctx, a.x2, a.y2, angle, head); // head at (x2,y2) = the press/start point
    } else if (a.type === "text") {
      const fs = Math.max(16, (c.width / 22) * TEXT_MUL[a.size || "m"]);
      ctx.font = `bold ${fs}px sans-serif`;
      ctx.textBaseline = "alphabetic";
      const tw = ctx.measureText(a.text).width;
      a.bbox = { x: a.x - 8, y: a.y - fs, w: tw + 16, h: fs + 12 };
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      const b = a.bbox; const r = 8;
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(b.x, b.y, b.w, b.h, r);
      } else {
        ctx.moveTo(b.x + r, b.y);
        ctx.arcTo(b.x + b.w, b.y, b.x + b.w, b.y + b.h, r);
        ctx.arcTo(b.x + b.w, b.y + b.h, b.x, b.y + b.h, r);
        ctx.arcTo(b.x, b.y + b.h, b.x, b.y, r);
        ctx.arcTo(b.x, b.y, b.x + b.w, b.y, r);
      }
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = col;
      ctx.fillText(a.text, a.x, a.y + 2);
    }
  };

  const boundsOf = (a) => {
    if (a.type === "text") return a.bbox || null;
    if (a.type === "circle" || a.type === "x") return { x: a.x, y: a.y, w: a.w, h: a.h };
    if (a.type === "arrow") {
      const x = Math.min(a.x1, a.x2), y = Math.min(a.y1, a.y2);
      return { x, y, w: Math.abs(a.x2 - a.x1), h: Math.abs(a.y2 - a.y1) };
    }
    if (a.type === "stroke" && a.points.length) {
      const xs = a.points.map((q) => q.x), ys = a.points.map((q) => q.y);
      const x = Math.min(...xs), y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    return null;
  };

  const redraw = () => {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    for (const a of actionsRef.current) drawShape(ctx, a, c);
    if (draftRef.current) drawShape(ctx, draftRef.current, c);
    const sel = selId != null ? find(selId) : null;
    if (sel) {
      ctx.save();
      if (sel.type === "arrow") {
        // draggable end-dots (rotate / restretch) — no bulky box
        const hr = Math.max(9, c.width / 95);
        ctx.fillStyle = "#22d3ee";
        ctx.strokeStyle = "#083344";
        ctx.lineWidth = Math.max(1.5, c.width / 500);
        for (const [hx, hy] of [[sel.x1, sel.y1], [sel.x2, sel.y2]]) {
          ctx.beginPath(); ctx.arc(hx, hy, hr, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        }
      } else {
        const b = boundsOf(sel);
        if (b) {
          const pad = 12;
          ctx.strokeStyle = "#22d3ee";
          ctx.lineWidth = Math.max(2, c.width / 480);
          ctx.setLineDash([10, 7]);
          ctx.strokeRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
        }
      }
      ctx.restore();
    }
  };

  useEffect(() => {
    const file = list[idx];
    if (!file) return;
    actionsRef.current = []; draftRef.current = null; setSelId(null); setNote(""); setHint(true); setDrawOn(!startLocked); setColorOpen(false);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      imgRef.current = img;
      const c = canvasRef.current;
      const w = Math.min(img.width, 1200);
      c.width = w; c.height = Math.round(img.height * (w / img.width));
      redraw();
    };
    img.src = url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { redraw(); }, [selId]);

  /* ---------- geometry helpers ---------- */
  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const distSeg = (p, x1, y1, x2, y2) => {
    const dx = x2 - x1, dy = y2 - y1;
    if (dx === 0 && dy === 0) return Math.hypot(p.x - x1, p.y - y1);
    let t = ((p.x - x1) * dx + (p.y - y1) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (x1 + t * dx), p.y - (y1 + t * dy));
  };
  const ccw = (a, b, c) => (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
  const segCross = (p1, p2, p3, p4) =>
    ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);

  const hitShape = (p) => {
    const c = canvasRef.current;
    for (let i = actionsRef.current.length - 1; i >= 0; i--) {
      const a = actionsRef.current[i];
      const tol = Math.max(24, strokeW(a, c) * 5);
      if (a.type === "text" && a.bbox && p.x >= a.bbox.x && p.x <= a.bbox.x + a.bbox.w && p.y >= a.bbox.y && p.y <= a.bbox.y + a.bbox.h) return a;
      if ((a.type === "circle" || a.type === "x") && p.x >= a.x - tol && p.x <= a.x + a.w + tol && p.y >= a.y - tol && p.y <= a.y + a.h + tol) return a;
      if (a.type === "arrow" && distSeg(p, a.x1, a.y1, a.x2, a.y2) < tol) return a;
      if (a.type === "stroke" && a.points.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < tol)) return a;
    }
    return null;
  };

  const translate = (a, dx, dy) => {
    if (a.type === "stroke") a.points.forEach((q) => { q.x += dx; q.y += dy; });
    else if (a.type === "arrow") { a.x1 += dx; a.y1 += dy; a.x2 += dx; a.y2 += dy; }
    else { a.x += dx; a.y += dy; }
  };

  /* ---------- stroke classification (smart snap) ---------- */
  const classify = (pts) => {
    if (pts.length < 2) return null;
    let len = 0;
    for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const bx = Math.min(...xs), by = Math.min(...ys);
    const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;
    const diag = Math.hypot(bw, bh);
    const first = pts[0], last = pts[pts.length - 1];
    const endGap = Math.hypot(last.x - first.x, last.y - first.y);
    if (len < 16 && diag < 16) return null; // stray tap
    const straightness = len > 0 ? endGap / len : 0;
    // straight line -> arrow (head at the START/press point, tail follows the drag)
    if (len > 26 && straightness > 0.82) {
      return { type: "arrow", x1: last.x, y1: last.y, x2: first.x, y2: first.y };
    }
    // closed loop -> ellipse
    if (diag > 34 && len > diag * 1.15 && endGap < diag * 0.4) {
      return { type: "circle", x: bx, y: by, w: bw, h: bh };
    }
    return { type: "stroke", points: pts };
  };

  const commit = (shape) => {
    if (!shape) { redraw(); return; }
    const base = { id: ++idRef.current, color, size, t: Date.now() };
    // Two quick crossing arrows -> X
    if (shape.type === "arrow") {
      const now = Date.now();
      for (let i = actionsRef.current.length - 1; i >= 0; i--) {
        const a = actionsRef.current[i];
        if (a.type !== "arrow") continue;
        if (now - (a.t || 0) > 1400) break;
        const cross = segCross(
          { x: a.x1, y: a.y1 }, { x: a.x2, y: a.y2 },
          { x: shape.x1, y: shape.y1 }, { x: shape.x2, y: shape.y2 }
        );
        if (cross) {
          actionsRef.current.splice(i, 1);
          const xs = [a.x1, a.x2, shape.x1, shape.x2], ys = [a.y1, a.y2, shape.y1, shape.y2];
          const bx = Math.min(...xs), by = Math.min(...ys);
          actionsRef.current.push({ ...base, type: "x", x: bx, y: by, w: Math.max(...xs) - bx, h: Math.max(...ys) - by });
          setHint(false); haptic();
          redraw();
          return;
        }
      }
    }
    actionsRef.current.push({ ...base, ...shape });
    setHint(false);
    if (shape.type !== "stroke") haptic();
    redraw();
  };

  /* ---------- pointer handling ---------- */
  const down = (e) => {
    if (!drawOn) return;
    e.preventDefault();
    const p = pos(e);
    const c = canvasRef.current;
    const sel = selId != null ? find(selId) : null;
    // grab a selected arrow's end-dot to rotate / restretch it
    if (sel && sel.type === "arrow") {
      const hr = Math.max(14, c.width / 60);
      if (Math.hypot(p.x - sel.x2, p.y - sel.y2) < hr) {
        dragRef.current = { a: sel, mode: "endpoint", which: "head", last: p }; return;
      }
      if (Math.hypot(p.x - sel.x1, p.y - sel.y1) < hr) {
        dragRef.current = { a: sel, mode: "endpoint", which: "tail", last: p }; return;
      }
    }
    const hit = hitShape(p);
    if (hit) {
      setSelId(hit.id);
      dragRef.current = { a: hit, last: p, moved: false, long: false };
      longRef.current = setTimeout(() => {
        if (dragRef.current && !dragRef.current.moved) {
          dragRef.current.long = true;
          if (hit.type === "text") editText(hit);
          else addLabel(hit);
        }
      }, 480);
      return;
    }
    setSelId(null);
    draftRef.current = { type: "stroke", points: [p], color, size };
  };

  const move = (e) => {
    const p = pos(e);
    if (dragRef.current) {
      e.preventDefault();
      const d = dragRef.current;
      if (d.mode === "endpoint") {
        if (d.which === "head") { d.a.x2 = p.x; d.a.y2 = p.y; }
        else { d.a.x1 = p.x; d.a.y1 = p.y; }
        redraw();
        return;
      }
      if (!d.moved && Math.hypot(p.x - d.last.x, p.y - d.last.y) > 5) { d.moved = true; clearTimeout(longRef.current); }
      if (d.moved && !d.long) { translate(d.a, p.x - d.last.x, p.y - d.last.y); d.last = p; redraw(); }
      return;
    }
    const d = draftRef.current;
    if (!d) return;
    e.preventDefault();
    d.points.push(p);
    redraw();
  };

  const up = () => {
    clearTimeout(longRef.current);
    if (dragRef.current) { dragRef.current = null; return; }
    const d = draftRef.current;
    draftRef.current = null;
    if (d) commit(classify(d.points));
  };

  /* ---------- text ---------- */
  const addLabel = (mark) => {
    setTextModal({
      title: "Add a label", value: "", placeholder: "Label text",
      onSubmit: (t) => {
        if (!t) return;
        const b = boundsOf(mark) || { x: mark.x || 40, y: mark.y || 40 };
        actionsRef.current.push({ id: ++idRef.current, type: "text", x: b.x, y: Math.max(28, b.y - 8), text: t, color, size });
        setHint(false); haptic(); redraw();
      },
    });
  };
  const addFreeText = () => {
    const c = canvasRef.current;
    setTextModal({
      title: "Add text", value: "", placeholder: "Type here",
      onSubmit: (t) => {
        if (!t) return;
        actionsRef.current.push({ id: ++idRef.current, type: "text", x: c.width * 0.2, y: c.height * 0.5, text: t, color, size });
        setHint(false); redraw();
      },
    });
  };
  const editText = (a) => {
    setTextModal({
      title: "Edit text", value: a.text || "", placeholder: "Text", allowDelete: true,
      onSubmit: (t) => {
        if (t === "") actionsRef.current = actionsRef.current.filter((x) => x !== a);
        else a.text = t;
        redraw();
      },
    });
  };
  const submitTextModal = () => {
    const v = (textModal?.value || "").trim();
    textModal?.onSubmit?.(v);
    setTextModal(null);
  };

  /* ---------- selection edits ---------- */
  const applyColor = (k) => {
    setColor(k);
    setColorOpen(false);
    const sel = selId != null ? find(selId) : null;
    if (sel) { sel.color = k; redraw(); }
  };
  const applySize = (k) => {
    setSize(k);
    const sel = selId != null ? find(selId) : null;
    if (sel) { sel.size = k; redraw(); }
  };
  const onTextBtn = () => {
    const sel = selId != null ? find(selId) : null;
    if (sel && sel.type === "text") editText(sel);
    else if (sel) addLabel(sel);
    else addFreeText();
  };
  const deleteSel = () => {
    if (selId == null) return;
    actionsRef.current = actionsRef.current.filter((a) => a.id !== selId);
    setSelId(null); redraw();
  };

  /* ---------- top / bottom actions ---------- */
  const undo = () => { actionsRef.current.pop(); setSelId(null); redraw(); };
  const reset = () => { actionsRef.current = []; setSelId(null); redraw(); };
  const next = () => (idx + 1 < list.length ? setIdx(idx + 1) : onDone());
  const close = () => {
    if (actionsRef.current.length || note.trim()) { setConfirmClose(true); return; }
    onCancel();
  };
  const save = () => {
    if (saving) return;
    const c = canvasRef.current, img = imgRef.current;
    if (!c) { onDone(); return; }   // canvas gone — nothing to export
    setSaving(true);
    try {
      // Render a clean frame first — image + shapes only, never the cyan
      // selection dots/box — so an un-deselected mark can't bake into the export.
      if (img) {
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        for (const a of actionsRef.current) drawShape(ctx, a, c);
      }
      setSelId(null);

      let settled = false;
      const finish = async (blob) => {
        if (settled) return;
        settled = true;
        if (!blob) {
          // Some mobile browsers hand back null under memory pressure — never
          // leave the button stuck on "Saving…"; let the user retry.
          setSaving(false);
          toast.error("Couldn't capture the photo — please try again");
          return;
        }
        try {
          await onSaveEach(blob, note.trim());
          setSaving(false);
          next();
        } catch {
          setSaving(false);   // onSaveEach reports its own upload error; stay open to retry
        }
      };
      // Safety net: if toBlob never invokes its callback, unstick after 10s.
      const guard = setTimeout(() => finish(null), 10000);
      c.toBlob((blob) => { clearTimeout(guard); finish(blob); }, "image/jpeg", 0.9);
    } catch {
      setSaving(false);
      toast.error("Couldn't save the photo — please try again");
    }
  };
  // Voice note via MediaRecorder + Whisper (works on iOS Safari, unlike Web Speech).
  const stopRec = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
  };
  const voice = async () => {
    if (recording) { stopRec(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice notes aren't supported on this browser");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch {
      toast.error("Microphone access was blocked");
      return;
    }
    streamRef.current = stream;
    const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", ""];
    const mime = types.find((t) => !t || (window.MediaRecorder?.isTypeSupported?.(t))) || "";
    let rec;
    try { rec = mime ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 24000 }) : new MediaRecorder(stream, { audioBitsPerSecond: 24000 }); }
    catch { try { rec = new MediaRecorder(stream); } catch { toast.error("Can't record on this device"); stream.getTracks().forEach((t) => t.stop()); return; } }
    recRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onerror = () => { toast.error("Recording error"); };
    rec.onstop = async () => {
      clearTimeout(recTimerRef.current);
      setRecording(false);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const type = rec.mimeType || mime || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      if (!blob.size) return;
      const ext = type.includes("mp4") || type.includes("aac") ? "mp4" : "webm";
      setTranscribing(true);
      try {
        const fd = new FormData();
        fd.append("audio", blob, `note.${ext}`);
        const hint = (navigator.language || "").slice(0, 2).toLowerCase();
        if (hint) fd.append("lang", hint);
        const { data } = await api.post("/transcribe", fd);
        const text = (data?.text || "").trim();
        if (text) setNote((n) => (n ? n + " " : "") + text);
        else toast.error("Didn't catch that — try again");
      } catch {
        toast.error("Couldn't transcribe — try again");
      } finally {
        setTranscribing(false);
      }
    };
    try { rec.start(); } catch { toast.error("Couldn't start recording"); stream.getTracks().forEach((t) => t.stop()); return; }
    setRecording(true);
    haptic();
    recTimerRef.current = setTimeout(stopRec, 30000); // safety auto-stop
  };
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } streamRef.current?.getTracks().forEach((t) => t.stop()); clearTimeout(recTimerRef.current); }, []);

  const hasSel = selId != null && !!find(selId);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[hsl(222,47%,7%)]" data-testid="photo-annotator">
      {/* top bar — pad for the iOS status bar / notch so undo/reset aren't hidden */}
      <div className="flex items-center justify-between px-4 pb-3 text-white" style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}>
        <button onClick={close} data-testid="annot-close" aria-label="Close"><X className="h-6 w-6" /></button>
        <span className="font-heading font-semibold">{title}{list.length > 1 ? ` · ${idx + 1}/${list.length}` : ""}</span>
        <div className="flex items-center gap-4">
          <button onClick={undo} data-testid="annot-undo" aria-label="Undo"><Undo2 className="h-5 w-5" /></button>
          <button onClick={reset} data-testid="annot-reset" aria-label="Clear all"><RotateCcw className="h-5 w-5" /></button>
        </div>
      </div>

      {/* canvas */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-3">
        <div className="relative max-h-full max-w-full overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/10">
          <canvas
            ref={canvasRef}
            className={cn("block max-h-full max-w-full touch-none", drawOn ? "cursor-crosshair" : "cursor-default")}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
          />
        </div>
        {hint && (
          <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center px-6" data-testid="annot-hint">
            <div className="animate-pulse rounded-full bg-black/65 px-4 py-2 text-center text-xs font-medium text-white backdrop-blur-sm">
              {drawOn
                ? "Just draw — a line becomes an arrow, a loop a circle, two slashes an ✕"
                : "Tap the ✏️ pencil to mark up — or just tap Save"}
            </div>
          </div>
        )}
        {hasSel && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-500/90 px-3 py-1 text-[11px] font-semibold text-white">
            Selected — drag the end-dots to rotate · recolor, resize or delete below
          </div>
        )}
      </div>

      {/* control rail — colors · sizes · text · delete */}
      <div className="flex items-center justify-center gap-2 px-3 py-2.5">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-2 backdrop-blur-md">
          {startLocked && (
            <>
              <button
                data-testid="annot-draw-toggle"
                onClick={() => setDrawOn((v) => !v)}
                aria-label={drawOn ? "Drawing on" : "Enable drawing"}
                className={cn(
                  "flex h-7 items-center justify-center gap-1 rounded-full px-2.5 text-xs font-semibold transition-transform active:scale-90",
                  drawOn ? "bg-emerald-500 text-white" : "bg-white/15 text-white/85"
                )}
              >
                <Pencil className="h-4 w-4" /> {drawOn ? "Drawing" : "Mark up"}
              </button>
              <span className="mx-1 h-6 w-px bg-white/15" />
            </>
          )}
          <div className="relative flex items-center">
            <button
              data-testid="annot-color-toggle"
              onClick={() => setColorOpen((o) => !o)}
              aria-label="Pick color"
              className={cn(
                "h-7 w-7 rounded-full border-2 transition-transform active:scale-90",
                colorOpen ? "border-white scale-110" : "border-white/40"
              )}
              style={{ backgroundColor: colorVal(color) }}
            />
            {colorOpen && (
              <>
                <button
                  className="fixed inset-0 z-[70] cursor-default"
                  aria-label="Close color picker"
                  onClick={() => setColorOpen(false)}
                />
                <div
                  data-testid="annot-color-row"
                  className="absolute bottom-full left-1/2 z-[75] mb-3 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-[hsl(222,44%,14%)] px-3 py-2.5 shadow-2xl"
                >
                  {COLORS.map((c) => (
                    <button
                      key={c.key}
                      data-testid={`annot-color-${c.key}`}
                      onClick={() => applyColor(c.key)}
                      aria-label={`Color ${c.key}`}
                      className={cn(
                        "h-8 w-8 rounded-full border-2 transition-transform active:scale-90",
                        color === c.key ? "border-white scale-110" : "border-white/30"
                      )}
                      style={{ backgroundColor: c.val }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <span className="mx-1 h-6 w-px bg-white/15" />
          {SIZES.map((s) => (
            <button
              key={s}
              data-testid={`annot-size-${s}`}
              onClick={() => applySize(s)}
              aria-label={`Size ${s}`}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full transition-transform active:scale-90",
                size === s ? "bg-white text-slate-900" : "text-white/70"
              )}
            >
              <span className="rounded-full bg-current" style={{ width: s === "s" ? 6 : s === "m" ? 10 : 15, height: s === "s" ? 6 : s === "m" ? 10 : 15 }} />
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-white/15" />
          <button
            data-testid="annot-text-btn"
            onClick={onTextBtn}
            aria-label="Add text"
            className="flex h-7 w-7 items-center justify-center rounded-full text-white/85 transition-transform active:scale-90"
          >
            <Type className="h-4 w-4" />
          </button>
          {hasSel && (
            <button
              data-testid="annot-delete-sel"
              onClick={deleteSel}
              aria-label="Delete selected"
              className="flex h-7 w-7 items-center justify-center rounded-full text-rose-400 transition-transform active:scale-90"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* note + save */}
      <div className="bg-[hsl(222,44%,11%)] px-4 pt-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
        <div className="relative">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="annot-note"
            placeholder="Add a note (optional)"
            className="h-11 w-full rounded-xl bg-white/10 px-3 pr-11 text-white outline-none placeholder:text-slate-400"
          />
          <button onClick={voice} disabled={transcribing} data-testid="annot-voice" aria-label={recording ? "Stop recording" : "Voice note"} className={cn("absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full transition-colors", recording ? "bg-rose-500 text-white animate-pulse" : "bg-white/10 text-slate-200 hover:bg-white/20")}>
            {transcribing ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-transparent" /> : <Mic className="h-4 w-4" />}
          </button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="annot-save" className="mt-3 h-12 w-full gap-1 bg-emerald-500 text-base font-semibold hover:bg-emerald-600">
          <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      {confirmClose && (
        <div className="absolute inset-0 z-[80] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm" data-testid="annot-discard-dialog">
          <div className="w-full max-w-xs rounded-2xl border border-white/10 bg-[hsl(222,44%,12%)] p-5 text-center shadow-2xl">
            <h3 className="font-heading text-lg font-bold text-white">Discard markup?</h3>
            <p className="mt-1 text-sm text-slate-400">Your annotations on this photo haven't been saved yet.</p>
            <div className="mt-5 flex gap-3">
              <button
                data-testid="annot-discard-cancel"
                onClick={() => setConfirmClose(false)}
                className="h-11 flex-1 rounded-xl border border-white/15 text-sm font-semibold text-white transition-transform active:scale-95"
              >
                Keep editing
              </button>
              <button
                data-testid="annot-discard-confirm"
                onClick={() => { setConfirmClose(false); onCancel(); }}
                className="h-11 flex-1 rounded-xl bg-rose-500 text-sm font-semibold text-white transition-transform active:scale-95"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {textModal && (
        <div className="absolute inset-0 z-[80] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm" data-testid="annot-text-dialog">
          <div className="w-full max-w-xs rounded-2xl border border-white/10 bg-[hsl(222,44%,12%)] p-5 shadow-2xl">
            <h3 className="font-heading text-base font-bold text-white">{textModal.title}</h3>
            <input
              autoFocus
              data-testid="annot-text-input"
              value={textModal.value}
              onChange={(e) => setTextModal((m) => ({ ...m, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") submitTextModal(); }}
              placeholder={textModal.placeholder || "Text"}
              className="mt-3 h-11 w-full rounded-xl bg-white/10 px-3 text-white outline-none placeholder:text-slate-400"
            />
            <div className="mt-4 flex gap-3">
              <button data-testid="annot-text-cancel" onClick={() => setTextModal(null)} className="h-11 flex-1 rounded-xl border border-white/15 text-sm font-semibold text-white transition-transform active:scale-95">Cancel</button>
              {textModal.allowDelete && (
                <button data-testid="annot-text-delete" onClick={() => { textModal.onSubmit(""); setTextModal(null); }} className="h-11 flex-1 rounded-xl bg-rose-500 text-sm font-semibold text-white transition-transform active:scale-95">Delete</button>
              )}
              <button data-testid="annot-text-save" onClick={submitTextModal} className="h-11 flex-1 rounded-xl bg-emerald-500 text-sm font-semibold text-white transition-transform active:scale-95">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
